import { FlexibleConnectedPositionStrategy } from '@angular/cdk/overlay';
import { AfterContentChecked, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { TooltipPosition } from '@angular/material/tooltip';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../../shared/dialogs/confirmation/confirmation-dialog.component';
import { AccessibilityHelper } from '../../accessibility.helper';
import { ScriptableBaseComponent } from '../../components/scriptable-base/scriptable-base.component';
import { BrowserStorageEnums } from '../../enums/browserstorage.enum';
import { FormDefinitionSpecialAction } from '../../enums/form-definition-special-action.enum';
import { FormFieldConfigurationSaveReason } from '../../enums/form-field-save-config-reason.enum';
import { IBrowserDriver } from '../../interfaces/ibrowser-driver.interface';
import { IContextMenuItem } from '../../interfaces/icontext-menu-item.interface';
import { IDrawerComponentLiaison } from '../../interfaces/idrawer-component-liaison';
import { IFieldDefinitionLogic } from '../../interfaces/ifield-definition-logic.interface';
import { AdditionalElementInfo, IAdditionalElementInfo, INameToPrettyNameMap, IOperationCompletedServiceMap, OperationCompletedServiceInfo } from '../../models/component-scripting/additional-element-info.model';
import { Scriptable } from '../../models/component-scripting/scriptable-function.annotation';
import { FormFieldAction } from '../../models/field-conditional-logic/form-field-action.model';
import { FormFieldExpression } from '../../models/field-conditional-logic/form-field-expression.model';
import { FormConditionalPropertiesDrawerComponentLiaison, FormFieldPropertiesDrawerComponentLiaison, FormPropertiesDrawerComponentLiaison } from '../../models/form-builder/drawers/drawer-classes';
import { FieldDefinition, FieldDefinitionAllowDropFlags } from '../../models/form-builder/field-definition.model';
import { FormFieldTypeEnum, FormFieldTypePrettyNamesEnum } from '../../models/form-builder/form-field-types';
import { FormField } from '../../models/form-builder/form-field.model';
import { LayoutCellInfo } from '../../models/form-builder/form-layout.model';
import { FormObjectDroppedFlags } from '../../models/form-builder/form-object-dropped-flags.model';
import { Form, IFormFieldIdToFormField } from '../../models/form-builder/form.model';
import { LayoutCell } from '../../models/form-builder/layout-cell.model';
import { LayoutRow } from '../../models/form-builder/layout-row.model';
import { FormBuilderRowContextMenuData } from '../../models/form-builder/menus/row-context-menu';
import { BrowserStorageService } from '../../services/browserstorage.service';
import { CurrentSiteService } from '../../services/current-site.service';
import { FieldDefinitionService } from '../../services/field-definition.service';
import { EnumUtil } from '../../utility-classes/enum-util';
import { ContextMenuComponent } from '../context-menu/context-menu.component';
import { FormFieldBaseComponent } from '../form-fields/form-field-base/form-field-base.component';
import { FormFieldWrapperComponent } from '../form-fields/form-field-wrapper/form-field-wrapper.component';
import { FlexibleSelectionFieldConfigurationComponent, FormFieldListConstraintColumn } from './field-configuration/flexible-selection-field-configuration/flexible-selection-field-configuration.component';
import { FieldConditionalLogicComponent } from './properties/field-conditional-logic/field-conditional-logic.component';
import { IFormBuilderAssistant } from './form-builder-assistant/iform-builder-assistant.interface';
import { NameFormFieldsDialog } from '../../dialogs/name-form-fields/name-form-fields.dialog';

declare let $: any; // jQuery

// Export a class.
export class FormBuilderSaveData {
    public formBuilderComponent: FormBuilderComponent = null;
    public canSaveFlag: boolean = false;

    public constructor(formBuilderComponentParam: FormBuilderComponent) {
        this.formBuilderComponent = formBuilderComponentParam;
    }
}

// Define an internally used enum that corresponds to the form mode's
// selected index, used only to make some code a bit more readable.
enum FormModeEnum {
    DESIGN_MODE = 0,
    PREVIEW_MODE = 1
}

// Define internally used classes to group properties.
class FormFieldProperties {
    // Properties.
    private _selectedField: FormField = null;
    public get SelectedField(): FormField {
        return this._selectedField;
    }
    public set SelectedField(value: FormField) {
        this._selectedField = value;
    }

    public savedUnchangedSelectedField: FormField = null;
    public applyingFormFieldProperties: boolean = false;
    public selectedFieldProperties: string[] = null;
    public selectedConstraintNames: string[] = null;
    public customRegexConstraintName: string = null;
    public selectedFieldDisplayFormatValues: string[] = null;
    public selectedFieldDisplayRowsValues: string[] = [];
    public selectedComponent: FormFieldBaseComponent = null;
    public deletedFields: string[] = null;
    public onEditSaveCallback: (formField: FormField) => void;

    // Constructor.
    public constructor() {
    }

    // Methods.
    // Note:  rather than updating form builder component lines of code
    //        to check for a null this.formFieldProperties value,
    //        let's just plan on resetting this structure's values.
    public reset() {
        this.SelectedField = null;
        this.savedUnchangedSelectedField = null;
        this.applyingFormFieldProperties = false;
        this.selectedFieldProperties = [];
        this.selectedConstraintNames = [];
        this.customRegexConstraintName = null;
        this.selectedFieldDisplayFormatValues = [];
        this.selectedFieldDisplayRowsValues = [];
        this.selectedComponent = null;
    }
}

// Define internally used classes.
class FBFreezeData {
    public readonly FREEZE_TOOLS_THRESHHOLD: number = 150;
    public freezeToolbox: string = null;
}

class FBMaxCellData {
    public iMaxCellCount: number = 0;
    public bNeedToCalculateMaxCellCount: boolean = true;
}

class FBEditFormData {
    public editForm: Form;
    public savedFormCopy: Form = null;
    public applyingFormSettings: boolean = false;
    public formIsBeingSaved: boolean = false;
}

@Component({
    selector: 'app-form-builder',
    templateUrl: './form-builder.component.html',
    styleUrls: ['./form-builder.component.scss'],
    standalone: false
})
export class FormBuilderComponent extends ScriptableBaseComponent implements OnInit, OnChanges, AfterContentChecked {
    // Properties.
    @Input()
    fieldDefinitions: FieldDefinition[];
    @Input()
    form: Form;
    @Input() formBuilderAssistant: IFormBuilderAssistant;
    @Input() formNameCanBeEdited: boolean = true;

    @Output()
    onSave: EventEmitter<FormBuilderComponent> = new EventEmitter();

    @ViewChild('drawer')
    drawer: MatDrawer;

    @ViewChild('formNameInput')
    formNameInput: ElementRef;

    private fieldGroups: string[];
    private deletedFields: FormField[];
    private freezeData: FBFreezeData = new FBFreezeData();
    private selectedIndex: number = 0;
    private isDirty: boolean = false;
    private isEditingFormName: boolean = false;
    private originalName: string;

    // Properties related to cell selection.
    private maxCellData: FBMaxCellData = new FBMaxCellData();

    private editFormData: FBEditFormData = new FBEditFormData();

    @ViewChild('rowContextMenuComponent') rowContextMenuComponent: ContextMenuComponent;
    private rowContextMenuData: FormBuilderRowContextMenuData = null;

    @ViewChildren(FormFieldWrapperComponent, { read: FormFieldWrapperComponent }) formFieldWrappers: QueryList<FormFieldWrapperComponent>;

    private formFieldProperties: FormFieldProperties = new FormFieldProperties();
    private draggedObject: any = null;

    private drawerComponentLiaison: IDrawerComponentLiaison = null;

    // Constructor.
    public constructor(private browserStorageService: BrowserStorageService,
        private dialog: MatDialog,
        private currentSiteService: CurrentSiteService,
        private fieldDefinitionService: FieldDefinitionService,
        elForBaseClass: ElementRef) // Note:  we only use this to get field type display names.
    {
        // Setup UI testing.
        super(elForBaseClass);
        let buttonNameMap: INameToPrettyNameMap =
        {
            'Design': 'Mode:  Design',
            'Preview': 'Mode: Preview',
            'settings': 'Form Settings ...',
            'save': 'Save Form'
        };
        let operationCompletedServiceMap: IOperationCompletedServiceMap =
        {
            'save': new OperationCompletedServiceInfo('formBuilderService', 'save')
        }
        this.element('#form-controls-div').hasButtons().withNameToPrettyNameMap(buttonNameMap).withOperationCompletedServiceMap(operationCompletedServiceMap);
        this.usesEnum('FormFieldTypeEnum').hasValues(EnumUtil.getEnumValues(FormFieldTypeEnum)).withPrettyNames(EnumUtil.getEnumValues(FormFieldTypePrettyNamesEnum));

        // Setup my context menu data.
        this.rowContextMenuData = new FormBuilderRowContextMenuData(this);

        return;
    }

    // Set freezeToolbox to true or false. In the template ngClass is used
    // to conditionally set classes on a couple of divs to fix the toolbox
    // to the top of screen.
    @HostListener("window:scroll")
    onWindowScroll() {
        let top = (document.documentElement.scrollTop || document.body.scrollTop);
        let sideNavOpen = false;

        //if (top >= this.FREEZE_TOOLS_THRESHHOLD) {
        if (top >= this.freezeData.FREEZE_TOOLS_THRESHHOLD) {
            if (this.browserStorageService.get(BrowserStorageEnums.SIDE_NAV_OPEN) == 'false') {
                //this.freezeToolbox = 'sideNavClosed';
                this.freezeData.freezeToolbox = 'sideNavClosed';
            } else {
                //this.freezeToolbox = 'sideNavOpen';
                this.freezeData.freezeToolbox = 'sideNavOpen';
            }
        } else {
            //this.freezeToolbox = null;
            this.freezeData.freezeToolbox = null;
        }
    }

    public ngOnInit(): void {
        //this.setupSearchForFieldGroupMatListItems(); // Must call this before calling super.ngOnInit().
        //super.ngOnInit();

        // 09-18-2020 note:  adding logic to generate client-only form field Ids.
        //                   These are needed by some components to be able to
        //                   uniquely address/identify form fields that do not
        //                   yet have a database-assigned 'Id' value.
        this.resetCellsWithNoFormField();
        this.assignFormFieldClientIds();
        this.assignConditionalLogicRuleClientIds();
        this.loadDeletedFormFields();

        // Note:  the following line is used during development only.  DO NOT ENABLE.
        //this.form.alwaysShowCellSelection = true;
    }

    public ngAfterViewInit(): void {
        // Note:  this method is handy as it allows us to check if @ViewChild() and @ViewChildren() values have been resolved.
        // Note:  the following line removes the tabs from the mat-tab control used in this component's HTML code.
        //        Removing the tabs does not have the negative side affect associated with using ng-deep to override
        //        the styling of the tabs.        
        $('#matTabGroup').find('div.mat-tab-label-container').remove();
    }

    // TODO -- there may be a better way to do this
    public ngAfterContentChecked(): void {
        AccessibilityHelper.focusOnMainContent();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes["fieldDefinitions"] && this.fieldDefinitions) {
            this.fieldGroups = [];
            this.fieldDefinitions.forEach(x => {
                if (!this.fieldGroups.includes(x.toolBoxGroup)) {
                    let visibleFieldGroupFieldDefs: FieldDefinition[] =
                        this.fieldDefinitions.filter(fd => (fd.toolBoxGroup == x.toolBoxGroup) && (fd.canBeDroppedOnForm || fd.canBeDroppedOnGrid || fd.canBeDroppedOnSiteHomePage));
                    if (visibleFieldGroupFieldDefs.length > 0)
                        this.fieldGroups.push(x.toolBoxGroup);
                }
            });

            super.ngOnInit();
        }

        if (changes["form"] && this.form) {
            this.form.formLayout.addBlankRow();
        }
    }

    public hasUnsavedChanges(): boolean {
        return this.isDirty;
    }

    // Accessor methods.
    public get Form(): Form {
        return (this.form);
    }

    public get DisableFormSave() {
        return !(this.form.CurrentUserCanBuildForms || this.form.currentUserCanEditHTML) || this.editFormData.formIsBeingSaved;
    }

    public get DisableFormSettings() {
        if (this.form.currentUserCanEditHTML && !this.form.CurrentUserCanBuildForms) return true;
        return false;
    }

    public get DisableFieldConditionalLogicRules() {
        if (this.form.currentUserCanEditHTML && !this.form.CurrentUserCanBuildForms) return true;
        return false;
    }

    public get DisableEditFormFieldNames() {
        if (this.form.currentUserCanEditHTML && !this.form.CurrentUserCanBuildForms) return true;
        return false;
    }

    public get FluidHeightTopOffset() {
        return 143; //vertical distance from window to top of element
    }

    public get IsEditingFormName() {
        return this.isEditingFormName;
    }

    public FieldDefDragScope(fieldDefinition: FieldDefinition): string[] {
        let fieldScopes: string[] = null;
        if (this.form.CurrentUserCanBuildForms) {
            if (fieldDefinition.canBeDroppedOnForm && fieldDefinition.canBeDroppedOnGrid)
                fieldScopes = ['field', 'gridField'];
            else if (fieldDefinition.canBeDroppedOnForm)
                fieldScopes = ['field'];
            else if (fieldDefinition.canBeDroppedOnGrid)
                fieldScopes = ['gridField'];
            else if (fieldDefinition.canBeDroppedOnSiteHomePage)
                fieldScopes = ['field'];
            else
                fieldScopes = [];
        } else {
            fieldScopes = [];
        }

        return (fieldScopes);
    }

    public DeletedFieldDragScope(): string[] {
        return (['field', 'gridField']);
    }

    public get TrashCanTooltip(): string {
        let tooltip: string = "Drag a field or a row here to delete it";

        return (tooltip);
    }

    public get DesignButtonTooltip(): string {
        let tooltip: string = "Use design mode to modify content and layout";

        return (tooltip);
    }

    public get PreviewButtonTooltip(): string {
        let tooltip: string = "Use preview mode to see how a form will function";

        return (tooltip);
    }

    public get CellSelectionCheckboxTooltip(): string {
        let tooltip: string =
            "Select cells to enable merge and separate cell operations.  " +
            "Use row properties to hide or show cell selection checkboxes."

        return (tooltip);
    }



    public get TooltipPosition(): TooltipPosition {
        return 'below';
    }
    public get FormFieldTooltipPosition(): TooltipPosition {
        return 'right';
    }

    public get DraggedObject(): any {
        return (this.draggedObject);
    }

    public get SelectedIndex(): number {
        return (this.selectedIndex);
    }
    public set SelectedIndex(indexParam: number) {
        this.selectedIndex = indexParam;

        return;
    }

    public get FreezeToolbox(): string {
        return (this.freezeData.freezeToolbox);
    }

    public get SelectedField(): FormField {
        return this.formFieldProperties.SelectedField;
    }
    public get SelectedFieldProperties(): string[] {
        return this.formFieldProperties.selectedFieldProperties;
    }
    public get SelectedConstraintNames(): string[] {
        return this.formFieldProperties.selectedConstraintNames;
    }
    public get CustomRegexConstraintName(): string {
        return this.formFieldProperties.customRegexConstraintName;
    }
    public get SelectedFieldDisplayFormatValues(): string[] {
        return this.formFieldProperties.selectedFieldDisplayFormatValues;
    }
    public get SelectedFieldDisplayRowsValues(): string[] {
        return this.formFieldProperties.selectedFieldDisplayRowsValues;
    }
    public get SelectedComponent(): FormFieldBaseComponent {
        return (this.formFieldProperties.selectedComponent);
    }

    public get EditForm(): Form {
        return this.editFormData.editForm;
    }
    public set EditForm(value: Form) {
        this.editFormData.editForm = value;
    }
    public GridCellClass(cell: LayoutCell): string {
        let cannotEditCellClass: string = '';
        let cellField: FormField = null;
        if ((cell.formFieldId != null) && (cell.formFieldId > 0)) {
            cellField = this.form.formFields.find(ff => ff.id == cell.formFieldId);
        }
        if ((cellField != null) && (!this.formBuilderAssistant.canEditField(this.fieldDefinitionService, cellField)))
            cannotEditCellClass = 'cannot-edit-cell';
        else if ((cellField == null) && this.formBuilderAssistant.canOnlyEditHtmlFields())
            cannotEditCellClass = 'cannot-edit-cell';

        let cellClasses: string = `${cell.gridCssClass} row-cell-div ${cannotEditCellClass}`;

        return (cellClasses);
    }

    public get ShowCellSelectionCheckboxes(): boolean {
        let bShow: boolean = this.form.enableCellSelection;

        if (bShow) {
            // Further check to see if either:
            //     1. The form layout has more than one column as a default; or
            //     2. There is at least one form field occupying more than one cell.
            if (this.maxCellData.bNeedToCalculateMaxCellCount) {
                this.calculateMaxCellCount();
                this.maxCellData.bNeedToCalculateMaxCellCount = false;
            }

            //**If there are 2+ cols that have at least 1 formfield in one of its cells
            //** OR if alwaysShowCellSelection checkbox = true display cell checkboxes */
            //bShow = ((this.maxCellData.iMaxCellCount > 1) || (this.form.alwaysShowCellSelection));

            bShow = this.form.alwaysShowCellSelection;
        }

        return (bShow);
    }

    public deletedFieldDisplayName(delField: FormField): string {
        let delFieldName: string = '';

        if ((delField.displayName != null) && (delField.displayName.trim() != '')) {
            delFieldName = delField.displayName.trim();
        }
        else if (delField.name != null) {
            delFieldName = delField.name.trim();
        }

        if (delFieldName.length > 12) {
            delFieldName = delFieldName.substring(0, 12) + '...';
        }

        return delFieldName;
    }

    private calculateMaxCellCount(): number {
        this.maxCellData.iMaxCellCount = this.form.formLayout.MaxRowCellCountWithContent;

        return this.maxCellData.iMaxCellCount;
    }

    public set ShowCellSelectionCheckboxes(bShowParam: boolean) {
        if (this.form) {
            this.form.enableCellSelection = bShowParam;
        }

        return;
    }

    public MergeCellsDisabledFor(row: LayoutRow): boolean {
        let iNumSelectedCells: number = 0;
        let iNumBlankCells: number = 0;

        row.cells.forEach(cell => {
            if (cell.isSelected) {
                iNumSelectedCells++;
            }
            if ((cell.formFieldId == 0) && (!cell.name)) {
                iNumBlankCells++;
            }
        });

        // Note:  we can only merge cells if:
        //
        //        1. At least two cells are selected; and
        //        2. The number of blank cells equals or
        //           exceeds the number of cells that would
        //           be removed by the merge.
        let iNumBlankCellsNeeded: number = (iNumSelectedCells - 1);
        let bDisabled: boolean =
            ((iNumSelectedCells < 2) || (iNumBlankCells < iNumBlankCellsNeeded));

        return (bDisabled);
    }

    public SeparateCellsDisabledFor(row: LayoutRow): boolean {
        // How many cells are presently selected?
        let iNumSelectedCells: number = 0;

        row.cells.forEach(cell => {
            if (cell.isSelected) {
                iNumSelectedCells++;
            }
        });

        // If the row only has one cell, and that cell has content,
        // see if the cell's field type can be split into multiple cells.
        let fieldCannotBeSplit: boolean = false;
        if ((row.cells.length == 1) && (row.cells[0].hasContent)) {
            let cellFormField: FormField = this.form.formFields.find(ff => ff.id == row.cells[0].formFieldId);
            if ((cellFormField != null) && cellFormField.cannotBeSplitOnForm)
                fieldCannotBeSplit = true;
        }

        // Note:  this method is not yet coded.
        // If only one cell is selected and the row cell count
        // is less than the default layout cell count, the
        // selected cell can be separated.
        let bDisabled: boolean =
            ((iNumSelectedCells != 1) ||
                (row.cells.length == this.form.formLayout.columnsDefault) ||
                fieldCannotBeSplit
            );

        return (bDisabled);
    }

    public get UseBetaFeatures(): boolean {
        return this.currentSiteService.Site.betaFeaturesEnabled;
    }

    public ShowFieldDefAsTool(fieldDef: FieldDefinition): boolean {
        let show: boolean = (fieldDef.canBeDroppedOnForm || fieldDef.canBeDroppedOnGrid || fieldDef.canBeDroppedOnSiteHomePage);
        return show;
    }

    public get FieldGroups(): string[] {
        let result = this.fieldGroups;
        return result;
    }
    public get FieldDefinitions(): FieldDefinition[] {
        let result = this.fieldDefinitions;
        return result;
    }
    public get InitiallyExpandedFieldGroup(): string {
        let result = this.formBuilderAssistant != null ? this.formBuilderAssistant.initiallyExpandedFieldGroup : 'Input Fields';
        return result;
    }

    public layoutCellStylesFor(cell: LayoutCell): string {
        let styles: string = '';

        if (cell.hasContent) {
            let cellFormField: FormField = this.form.formFields.find(ff => ff.name == cell.name);
            if (cellFormField != null) {
                let fieldDefClientLogic: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldClientLogicHandler(cellFormField.fieldDefinitionClassName);
                if (fieldDefClientLogic.hasCustomFormLayoutStylesFor(cellFormField))
                    styles = fieldDefClientLogic.customFormLayoutStylesFor(cellFormField);

                //VNEXT-1618
                if (cellFormField.fieldDefinitionClassName != "Collect.vNext.FieldDefinitions.RawHTMLFieldDefinition") {
                    if (!this.form.CurrentUserCanBuildForms && this.form.currentUserCanEditHTML) {
                        styles += " opacity: 30%; pointer-events: none; background: grey; ";
                    }
                }
            }
        }

        return styles;
    }

    public get RowContextMenuData(): IContextMenuItem[] {
        return this.rowContextMenuData.RowContextMenuData;
    }

    public get DrawerComponentLiaison(): IDrawerComponentLiaison {
        return this.drawerComponentLiaison;
    }
    public set DrawerComponentLiaison(value: IDrawerComponentLiaison) {
        this.drawerComponentLiaison = value;
    }

    public get FormFieldProperties(): FormFieldProperties {
        return this.formFieldProperties;
    }

    public get ApplyingFormSettings(): boolean {
        return this.editFormData.applyingFormSettings;
    }
    public set ApplyingFormSettings(value: boolean) {
        this.editFormData.applyingFormSettings = value;
    }
    public get FormIsBeingSaved(): boolean {
        return this.editFormData.formIsBeingSaved;
    }
    public set FormIsBeingSaved(value: boolean) {
        this.editFormData.formIsBeingSaved = value;
    }

    public get SavedFormCopy(): Form {
        return this.editFormData.savedFormCopy;
    }
    public set SavedFormCopy(value: Form) {
        this.editFormData.savedFormCopy = value;
    }

    public get DeletedFields(): FormField[] {
        return this.deletedFields;
    }

    public sanitizeFieldGroupName(fieldGroupName: string): string {
        let result: string = fieldGroupName.replace(/ /g, '_').replace(/\//g, '_');
        return result;
    }

    public get CanEditTemplateProperties(): boolean {
        return this.formBuilderAssistant != null ? this.formBuilderAssistant.canEditTemplateProperties() : false;
    }

    public canEditField(formFieldId: number): boolean {
        let formField: FormField = this.form.formFields != null ? this.form.formFields.find(ff => ff.id == formFieldId) : null;
        let canEdit: boolean = formField != null ? this.formBuilderAssistant.canEditField(this.fieldDefinitionService, formField) : false;
        return canEdit;
    }
    // End methods called by my HTML code.

    // Drag and drop methods.
    public onDropRow(e: any, row: LayoutRow) {
        if (e.dragData != row) {
            let movingRow = e.dragData as LayoutRow;

            this.moveRow(movingRow, row);
        }
    }

    public onDrop(e: any, row: LayoutRow, dropCell: LayoutCell) {
        let objectDroppedFlags = new FormObjectDroppedFlags();

        if (e.dragData instanceof FieldDefinition) {
            this.handleFieldDefDrop(row, dropCell, e.dragData, objectDroppedFlags);
        }
        else if (e.dragData instanceof LayoutCell) {
            this.handleCellDrop(dropCell, e.dragData);
        }
        else if (e.dragData instanceof FormField) {
            this.handleFormFieldDrop(dropCell, e.dragData);
        }

        if (row.isLastRow && (!objectDroppedFlags.skipAddLastRow)) {
            row.isLastRow = false;
            this.form.formLayout.addBlankRow();
        }
        this.flagDirty();
    }

    public handleFieldDefDrop(row: LayoutRow, dropCell: LayoutCell, definition: FieldDefinition, objectDroppedFlags: FormObjectDroppedFlags) {
        let allowsNestedDropFlags: FieldDefinitionAllowDropFlags = null;
        let droppedOnFieldDefCustomLogic: IFieldDefinitionLogic = null;
        if (dropCell.hasContent) {
            let droppedOnFormField: FormField = this.form.formFields.find(ff => ff.name == dropCell.name);

            if (droppedOnFormField != null) {
                droppedOnFieldDefCustomLogic = this.fieldDefinitionService.getFieldClientLogicHandler(droppedOnFormField.fieldDefinitionClassName)
                allowsNestedDropFlags = new FieldDefinitionAllowDropFlags();
                droppedOnFieldDefCustomLogic.checkAllowsNestedDrop(definition, allowsNestedDropFlags);
            }
        }

        if (dropCell.hasContent && (allowsNestedDropFlags != null) && allowsNestedDropFlags.allowDrop && definition.hasBeginAndEndElements) {
            // Only begin and end fields allow nested drops.
            this.performBeginAndEndElementDrop(row, dropCell, definition);
        } else if (dropCell.hasContent && (!definition.hasSpecialFormAction)) {
            // Ticket VNEXT-217:  if the cell already contains a field prompt the user to confirm the drop.
            let confirmationTitle: string = 'Replace Existing Field?';
            let confirmationMessage: string = "Are you sure you want to replace the cell's existing field?";
            let confirmationData: ConfirmationDialogModel = new ConfirmationDialogModel(confirmationTitle, confirmationMessage);

            const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: confirmationData });
            dialogRef.afterClosed().subscribe(yes => {
                if (yes)
                    this.performFieldDrop(dropCell, definition);
            });
        } else if (definition.canBeDroppedOnFormAsCell)
            this.performFieldDrop(dropCell, definition);
        else if (definition.hasBeginAndEndElements)
            this.performBeginAndEndElementDrop(row, dropCell, definition);
        else if (definition.hasSpecialFormAction)
            this.performSpecialFormAction(row, dropCell, definition, objectDroppedFlags);
    }

    public handleCellDrop(dropCell: LayoutCell, movedCell: LayoutCell) {
        if (movedCell != dropCell) {
            let tmpCell: LayoutCell = new LayoutCell();

            if (dropCell.formFieldId > 0 || dropCell.name != '') {
                // cell already contains a field
                tmpCell = <LayoutCell>dropCell.clone();
            }

            dropCell.formFieldId = movedCell.formFieldId;
            dropCell.name = movedCell.name;

            movedCell.formFieldId = tmpCell.formFieldId;
            movedCell.name = tmpCell.name;
        }
    }

    public handleFormFieldDrop(dropCell: LayoutCell, formField: FormField) {
        if (dropCell.formFieldId > 0) {
            let droppedOnFormField: FormField = this.form.formFields.find(ff => ff.id == dropCell.formFieldId);
            if (droppedOnFormField) {
                this.DeletedFields.push(droppedOnFormField);
            }
        }

        let field = this.undeleteField(dropCell, formField);
        this.addExistingField(dropCell, field);

        //remove restored field from deleted fields list
        this.deletedFields = this.deletedFields.filter(function (item) {
            return item.id !== field.id;
        });

        dropCell.formFieldId = field.id;
        dropCell.name = field.name;
    }

    // Flags the FormBuilder as having unsaved changes
    public flagDirty() {
        this.isDirty = true;
        this.maxCellData.bNeedToCalculateMaxCellCount = true;
    }

    public clearDirtyFlag() {
        this.isDirty = false;
        this.maxCellData.bNeedToCalculateMaxCellCount = true;
    }

    public onDragStart(e: any, obj: any) {
        this.form.formLayout.clearSelection();
        this.draggedObject = obj;
    }

    public onRowDragStart(e: any, row: LayoutRow): void {
        this.onDragStart(e, row);
    }

    public onRowCellDragStart(e: any, row: LayoutRow, cell: LayoutCell): void {
        this.onDragStart(e, cell);
    }

    public onDragEnd(e: any) {
        this.draggedObject = null;
    }

    public getDragClass(obj: any, defaultClass: string) {
        return obj == this.draggedObject ? 'drop-not-allowed' : defaultClass;
    }

    public onObjectDroppedOnTrash(event: any): void {
        if (this.draggedObject) {
            if (this.draggedObject instanceof LayoutRow) {
                this.deleteRow(<LayoutRow>this.draggedObject);

                // If we just deleted the last, blank row, re-add it.
                let iNumRows: number = this.form.formLayout.rows.length;
                let lastRow: LayoutRow = this.form.formLayout.rows[iNumRows - 1];

                if (lastRow.hasContent) {
                    // We need to create a new blank row.
                    this.form.formLayout.addBlankRow();
                }
            } else if (this.draggedObject instanceof LayoutCell) {
                this.deleteCellContent(<LayoutCell>this.draggedObject);
            }

            this.draggedObject = null;
        }
        return;
    }

    // Selection code.
    public onCellSelect(event: any, cell: LayoutCell): void {
        event.stopPropagation();
        this.selectCell(cell);
    }

    public onCellSelectionCheckboxChanged(cell: LayoutCell): void {
        // Note:  presently this method is a NOOP.
    }

    public onFieldDefinitionDoubleClick(fieldDefinition: FieldDefinition): boolean {
        let fieldAdded: boolean = false;

        let selectedCells: LayoutCell[] = this.form.formLayout.selectedCells;
        if ((selectedCells != null) && (selectedCells.length == 1) && selectedCells[0].isSelected && (!selectedCells[0].hasContent)) {
            let selectedCell: LayoutCell = selectedCells[0];
            let selectedCellRow: LayoutRow = this.form.formLayout.getCellRowFor(selectedCell);
            if (selectedCellRow != null) {
                let dropData = { dragData: fieldDefinition };
                this.onDrop(dropData, selectedCellRow, selectedCell);
                fieldAdded = true;
            }
        }

        return fieldAdded;
    }

    public onCellSelectionCheckboxClicked(event: any, cell: LayoutCell): void {
        event.stopPropagation();
    }
    public onCellSelectionCheckboxDoubleClicked(event: any, cell: LayoutCell): void {
        event.stopPropagation();

        cell.isSelected = true;
        // Unselect any other selected cells.
        this.form.formLayout.clearSelection(cell);
    }

    public onRowSelect(event: any, row: LayoutRow): void {
        event.stopPropagation();

        this.form.formLayout.clearSelection();
        row.isSelected = true;
    }

    private lookedForFields: string[] = [];
    private foundFields: string[] = [];
    private notFoundFields: string[] = [];

    /**
    * Gets a form field on the page using the specified params
    * @param id - Id assigned to the formfield
    * @param name - internal name assigned to the formfield
    */
    public getFormField(id: number, name: string): FormField {
        let ff: FormField;
        let list: FormField[] = this.form.formFields.filter(x => (id && id > 0) ? x.id == id : x.name == name);

        if (this.lookedForFields.indexOf(name) == -1) {
            //console.log('Looking for id=' + id + ', name =' + name + " in...");
            //console.log(this.form.formFields.map(x => [x.id, x.name].join(',')));
            this.lookedForFields.push(name);
        }

        if ((list != null) && (list.length != 0)) {
            ff = list[0];
            if (this.foundFields.indexOf(name) == -1) {
                //console.log('   ==> Found id=' + id + ', name =' + name + ' in...');
                //console.log(this.form.formFields.map(x => [x.id, x.name].join(',')));
                this.foundFields.push(name);
            }
        }
        else {
            if (this.notFoundFields.indexOf(name) == -1) {
                //console.log('   ==> DID NOT Find id=' + id + ', name =' + name + ' in...');
                //console.log(this.form.formFields.map(x => [x.id, x.name].join(',')));
                this.notFoundFields.push(name);
                //debugger;
            }
        }
        return ff;
    }

    public formFieldOnInit(e: any, cell: LayoutCell) {
        return;
    }

    public formModeSelectionChanged() {
        if (this.selectedIndex == FormModeEnum.PREVIEW_MODE) {
            // For any components requiring a call
            // to saveConfiguration(), call that now.
            this.tellFormFieldsToSave(FormFieldConfigurationSaveReason.SwitchingToFormPreviewMode);
        }

        return;
    }

    public doFlexConfig(formFieldId: number, formFieldName: string) {
        this.OpenFlexConfigPanel(formFieldId, formFieldName);
    }

    public onCellDoubleClick(cell: LayoutCell) {
        this.OpenFlexConfigPanel(cell.formFieldId, cell.name);
    }

    // See corresponding method (onCellDoubleClick()) in grid-field-designer.component.ts for grid-level fields
    public OpenFlexConfigPanel(formFieldId: number, cellName: string) {
        // TODO: Note, this call to getFormField does not get flex fields in grid, and so they do not subscribed to, hence
        // when you click "Save Template" on a flex field in a grid, the saving of the Form is NOT triggered
        // Either expand getFormField() to include grid fields, or add a call to another method to get grid-level fields
        let ff = this.getFormField(formFieldId, cellName);
        if (ff && ff.fieldHasConfiguration) {
            let dialogConfig: MatDialogConfig = new MatDialogConfig();
            dialogConfig.disableClose = true;
            dialogConfig.minWidth = '960px';
            dialogConfig.data = {
                autoCompleteEnabled: ff.autocomplete,
                formTemplateId: this.form.id,
                name: ff.name,
                id: ff.id,
                displayName: ff.displayName,
                toolTip: ff.toolTip,
                placeholderText: ff.placeholderText,
                instructionsText: ff.instructionsText,
                hasFootnote: ff.hasFootnote,
                formFields: this.form.formFields,
                formField: ff,
                listValuesConstraintId: ff.listValuesConstraintId,
                listValuesConstraintName: ff.listValuesConstraintName,
                listConstraintColumnName: ff.listConstraintColumnName,
                dependsOnParentFormFieldId: ff.dependsOnParentFormFieldId,
                dependsOnParentFormFieldName: ff.dependsOnParentFormFieldName,
                ListConstraintColumnId: ff.ListConstraintColumnId,
                CascadingDropdownConstraintId: ff.cascadingDropdownConstraintId,
                fieldColumnPairs: ff.formFieldListConstraintColumns,
                maximumSelections: ff.maxSelections,
                required: ff.required,
                defaultValue: ff.defaultValue != null ? ff.defaultValue : ""
            };
            //***Open modal
            const dialogRef = this.dialog.open(FlexibleSelectionFieldConfigurationComponent, dialogConfig);
            dialogRef.afterClosed().subscribe(result => {
                if (result != null) {
                    //***Populate fields with existing values
                    if (result.constraintIsSimpleList) {
                        ff.cascadingDropdownConstraintId = 0;
                        ff.listValuesConstraintId = result.selectConstraintId;
                    }
                    else {
                        ff.cascadingDropdownConstraintId = result.selectConstraintId ?? 0;
                        ff.listValuesConstraintId = result.listValuesConstraintId;
                    }

                    ff.dependsOnParentFormFieldId = result.dependsOnParentFormFieldId;
                    ff.dependsOnParentFormFieldName = result.dependsOnParentFormFieldName;
                    ff.dependsOnParentFormFieldDefinitionClassName = result.dependsOnParentFormFieldDefinitionClassName;

                    if (result.displayName) {
                        ff.displayName = result.displayName;
                    }
                    if (result.name) {
                        //***If internal name changed, update form value and the layout values
                        if (ff.name != result.name) {
                            let origName: string = ff.name;
                            ff.name = result.name;
                            this.form.formLayout?.fieldNameUpdated(origName, result.id, result.name);
                        }
                    }
                    ff.toolTip = result.toolTip;
                    ff.placeholderText = result.placeholderText;
                    ff.instructionsText = result.instructionsText;
                    ff.hasFootnote = result.hasFootnote;
                    ff.required = result.required;
                    ff.defaultValue = result.defaultValue;
                    ff.autocomplete = result.autoCompleteEnabled;

                    ff.formFieldListConstraintColumns = [];
                    for (let index: number = 0; index < result.fieldColumnPairs.length; index++) {
                        let columnId = result.fieldColumnPairs[index].listConstraintColumnId;
                        if (ff.formFieldListConstraintColumns.filter(col => col.formFieldId == ff.id && col.listConstraintColumnId == columnId).length == 0) {
                            let newCol = new FormFieldListConstraintColumn(ff.id, columnId);
                            newCol.listConstraintColumnName = result.fieldColumnPairs[index].listConstraintColumnName;
                            ff.formFieldListConstraintColumns.push(newCol);
                        }
                    }
                    // was a metadata field added? If so, add it to the form
                    if (result.selectedMetadataField) {
                        let existingMetadataField = this.form.formFields.find(x => x.id == result.selectedMetadataField?.id);
                        if (!existingMetadataField) {
                            this.form.formFields.push(result.selectedMetadataField);
                        }
                    }

                    ff.maxSelections = result.maximumSelections;

                    this.save();
                }
            });
        }
        return;
    }

    public formFieldEdit(e: any, cell: LayoutCell) {
        this.formFieldProperties.SelectedField = this.form.findFormField(e.formField.formFieldNumberOnClient);
        if (this.formFieldProperties.SelectedField == null)
            this.formFieldProperties.SelectedField = e.formField;

        this.formFieldProperties.savedUnchangedSelectedField = Object.assign({}, this.formFieldProperties.SelectedField);  //<FormField>this.formFieldProperties.selectedField.clone();
        this.formFieldProperties.applyingFormFieldProperties = false;

        this.formFieldProperties.selectedFieldProperties = e.properties ? e.properties : [];
        this.formFieldProperties.selectedConstraintNames = e.constraintNames ? e.constraintNames : []
        this.formFieldProperties.customRegexConstraintName = e.customRegexConstraintName ? e.customRegexConstraintName : null;
        this.formFieldProperties.selectedFieldDisplayFormatValues = e.displayFormatValues ? e.displayFormatValues : [];
        this.formFieldProperties.selectedFieldDisplayRowsValues = e.displayRowsValues ? e.displayRowsValues : [];
        this.formFieldProperties.selectedComponent = e.component;
        this.formFieldProperties.onEditSaveCallback = (e.onEditSaveCallback ? e.onEditSaveCallback : null);

        this.selectCell(cell);

        this.drawerComponentLiaison = new FormFieldPropertiesDrawerComponentLiaison(this);

        this.drawer.open();
    }
    public noopFormFieldEdit(e: any, cell: LayoutCell): void {
        // Note:  this method should not even be called.
        // Note:  this method does nothing by design.
    }

    public save() {
        this.editFormData.formIsBeingSaved = true;
        // Allow form fields to save their configurations.
        this.tellFormFieldsToSave(FormFieldConfigurationSaveReason.SavingFormToServer);

        // Now save the form configuration.
        // 01-31-2021 change:  only pop the last
        //                     row if it is empty.
        let iNumRows: number = this.form.formLayout.rows.length;
        let lastRow: LayoutRow = this.form.formLayout.rows[iNumRows - 1];

        let bLastRowIsEmpty: boolean = (!lastRow.hasContent);

        if (bLastRowIsEmpty) {
            this.form.formLayout.rows.pop();
        }

        // Clear any "Show To"/"Hide From" transient properties.
        this.form.clearTransientProperties();

        this.form.jsonConfig = JSON.stringify({
            hideSaveButton: this.form.hideSaveButton,
            saveButtonCaption: this.form.saveButtonCaption,
            confirmSavePopup: this.form.confirmSavePopup,
            enableBulkWorkflow: this.form.enableBulkWorkflow
        });

        // Ask my parent to save.
        this.onSave.emit(this);
        // Done.
        return;
    }

    // Note:  the following method is called from another component.
    public saveCompleted(formParam: Form): void {
        // Save the provided form.
        this.form = formParam;

        // Reassign id values.
        // Note:  it's important to update the caller's
        //        form value as it gets passed again as
        //        input to this component.  Hence we want
        //        to make sure we add the client id values
        //        to the caller's copy of the form as well.
        this.assignFormFieldClientIds();
        this.assignConditionalLogicRuleClientIds();
        this.clearDirtyFlag();
        this.form.restoreTransientProperties();
        this.editFormData.formIsBeingSaved = false;
        this.tellFormFieldsSaveCompleted();
        return;
    }

    public saveCouldNotBePerformed(): void {
        this.editFormData.formIsBeingSaved = false;
        this.form.formLayout.addBlankRow();
    }

    public updateRowsWithFullWidthFields(): void {
        if (this.form.formLayout.rows != null) {
            for (let rowIndex: number = 0; rowIndex < this.form.formLayout.rows.length; rowIndex++) {
                let layoutRow: LayoutRow = this.form.formLayout.rows[rowIndex];

                if ((layoutRow.cells != null) && (layoutRow.cells.length > 0)) {
                    let firstCell: LayoutCell = layoutRow.cells[0];
                    if (firstCell.hasContent) {
                        let cellFormField: FormField = this.form.formFields.find(ff => ff.name == firstCell.name);
                        if (cellFormField != null) {
                            let fieldDef: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(cellFormField.fieldDefinitionClassName);
                            if ((fieldDef != null) && fieldDef.occupiesEntireFormRow) {
                                if (layoutRow.cells.length > 1) {
                                    this.mergeRowCellsClick(layoutRow, false);
                                    //layoutRow.cells[0].transientCellOccupiedByFullRowWidthField = true;
                                } // if
                            } // if
                        } // if                         
                    } // if
                } // if
            } // for
        }
    }

    public onContextMenu(event: MouseEvent, row: LayoutRow) {
        // Show the context menu.
        event.preventDefault();

        // 11-04-2022 note:  will enable the following code after having
        //                   completed more tests using the context menu component.
        let contextMenuPositionX = event.clientX + 'px';
        let contextMenuPositionY = event.clientY + 'px';
        if (this.rowContextMenuComponent != null)
            this.rowContextMenuComponent.openMenuForItemAt(row, contextMenuPositionX, contextMenuPositionY);
    }

    public moveRowClick(row: LayoutRow, direction: number) {
        let newPos = row.position + direction;

        let toRow = this.form.formLayout.rows.filter(x => x.position == newPos)[0];

        if (toRow) {
            this.moveRow(row, toRow);
        }
        else {
            console.log('moveRowClick() error');
        }
    }

    private moveRow(fromRow: LayoutRow, toRow: LayoutRow): void {
        let oldPosition: number = fromRow.position;
        let newPosition: number = toRow.position;

        if (this.form.formLayout.moveRow(oldPosition, newPosition)) {
            fromRow.position = newPosition;

            this.form.formLayout.rows = this.form.formLayout.rows.sort((x, y) => x.position < y.position ? -1 : x.position > y.position ? 1 : 0);
            this.flagDirty();

            if (toRow.isLastRow || fromRow.isLastRow) {
                if (toRow.isLastRow) {
                    toRow.isLastRow = false;
                }
                else if (fromRow.isLastRow) {
                    fromRow.isLastRow = false;
                }

                this.form.formLayout.addBlankRow();
            }
        }
    }

    public insertRowClick(index: number): void {
        this.form.formLayout.addBlankRow(index);
        this.flagDirty();
    }

    public mergeRowCellsClick(row: LayoutRow, mergeOnlySelectedCells: boolean = true): void {
        // Find the cell with content, if any.
        let iNumSelectedCells: number = 0;
        let iTotalWidthOfMergingCells: number = 0;
        let cellWithContent: LayoutCell = null;

        for (let iCell: number = 0; iCell < row.cells.length; iCell++) {
            let cell: LayoutCell = row.cells[iCell];

            if (cell.isSelected || (!mergeOnlySelectedCells)) {
                iNumSelectedCells++;
                iTotalWidthOfMergingCells += cell.width;

                if ((cellWithContent == null) && cell.hasContent) {
                    cellWithContent = cell;
                } // if
            } // if
        } // for

        // Build a new row cells array.
        let newCells: LayoutCell[] = [];
        let iNumMergingCellsRemaining: number = iNumSelectedCells;
        let bCellsMerged: boolean = false;

        for (let iCell: number = 0; iCell < row.cells.length; iCell++) {
            let cell: LayoutCell = row.cells[iCell];

            if (cell.isSelected || (!mergeOnlySelectedCells)) {
                // If this is the selected cell with content,
                // if any have content, or we are down to one
                // cell left to merge, complete the merge here.
                if (cell.hasContent || (iNumMergingCellsRemaining == 1)) {
                    if (!bCellsMerged) { // Only perform one merge operation.
                        // We are either on the lone selected 
                        // cell or the last merging cell.

                        // Set the cell's CSS class from its width.
                        cell.gridCssClass = `col-${iTotalWidthOfMergingCells}`;

                        // If there is a cell with content that is not this
                        // cell, assign the existing content to this cell.
                        if ((cellWithContent != null) && (!cell.hasContent)) {
                            cell.formFieldId = cellWithContent.formFieldId;
                            cell.name = cellWithContent.name;
                        }

                        //***Uncheck cell
                        cell.isSelected = false;

                        newCells.push(cell);
                        bCellsMerged = true;
                    }
                }

                iNumMergingCellsRemaining--;
            } else {
                // Include any unselected cells.
                newCells.push(cell);
            }
        } // for

        // Finally, replace the row's cells with the newly constructed one.
        row.cells = newCells;

        return;
    }

    public separateRowCellsClick(row: LayoutRow): void {
        // Build a new row cells array.
        let iNumCellsToAdd: number = (this.form.formLayout.columnsDefault - row.cells.length);
        let newCells: LayoutCell[] = [];

        for (let iCell: number = 0; iCell < row.cells.length; iCell++) {
            let cell: LayoutCell = row.cells[iCell];

            if (cell.isSelected) {
                let iSelectedCellWidth: number = cell.width;
                let newCellWidth: number = (iSelectedCellWidth / (1 + iNumCellsToAdd));
                cell.gridCssClass = `col-${newCellWidth}`;

                newCells.push(cell);

                while (iNumCellsToAdd > 0) {
                    let newBlankCell: LayoutCell = new LayoutCell();
                    newBlankCell.gridCssClass = cell.gridCssClass;

                    //***Uncheck cell
                    cell.isSelected = false;

                    newCells.push(newBlankCell);

                    iNumCellsToAdd--;
                } // while
            } else {
                newCells.push(cell);
            } // if-else
        } // for

        // Replace the row's cells with the newly constructed one.
        row.cells = newCells;

        return;
    }

    public deleteRow(row: LayoutRow): void {
        row.cells.forEach(delCell => {
            let ff: FormField = (delCell.formFieldId != null) && (delCell.formFieldId != 0) ? this.getFormField(delCell.formFieldId, delCell.name) : null;
            if (ff) {
                ff.isDeleted = true;
                this.DeletedFields.push(ff);
            }
        });

        this.form.formLayout.deleteRow(row.position - 1);
        this.flagDirty();
    }

    public deleteBlankRows(): void {
        this.form.formLayout.deleteBlankRows();
    }

    public drawerClosing(): void {
        // Did we cancel out of a drawer?
        // We might have to undo any changes made.
        if (!this.formFieldProperties.applyingFormFieldProperties) {
            if ((this.formFieldProperties.SelectedField != null) &&
                (this.formFieldProperties.savedUnchangedSelectedField != null) &&
                (this.formFieldProperties.selectedComponent != null)) {
                //***Reset values for the formfield including formfield properties displayed in the form
                this.formFieldProperties.selectedComponent.resetFormField(this.formFieldProperties.savedUnchangedSelectedField);
            }
        }
        else if (!this.editFormData.applyingFormSettings) {
            if (this.editFormData.savedFormCopy != null) {
                this.form = this.editFormData.savedFormCopy;
                this.editFormData.savedFormCopy = null;
            }
        }

        // Reset properties.
        this.formFieldProperties.reset();
        this.editFormData.editForm = null;
    }

    public formSettingsClick(): void {
        this.editFormData.editForm = this.form;
        this.editFormData.savedFormCopy = <Form>this.form.clone();
        this.drawerComponentLiaison = new FormPropertiesDrawerComponentLiaison(this, this.formBuilderAssistant);
        this.drawer.open();
    }

    private static findFormFieldInForm(form: Form, eventFormField: FormField): FormField {

        let arrFormField: FormField[] = null;
        if (eventFormField.id && (eventFormField.id > 0)) {
            arrFormField = form.formFields.filter(ff => ff.id == eventFormField.id);
        } else {
            arrFormField = form.formFields.filter(ff => ff.name == eventFormField.name);
        }
        if ((!arrFormField) || (arrFormField.length != 1)) {
            let errorMsg: string =
                `FormBuilderComponent.formFieldConditionalLogicEdit():  cannot find form field ` +
                `'${eventFormField.name}' (id = ${eventFormField.id}).`;
            throw (errorMsg);
        }

        return arrFormField[0];
    }

    /*
    public formFieldConditionalLogicEdit(e: any, cell: LayoutCell): void {
        this.editFieldConditionalLogicRules();
    }
    */
    public editFieldConditionalLogicRules(): void {
        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.data = {
            form: this.form,
            formBuilderAssistant: this.formBuilderAssistant
        };
        const dialogRef = this.dialog.open(FieldConditionalLogicComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(result => {
            if (result != null) {
                this.save();
            }
        });
    }

    public editFormFieldNames(): void {
        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.disableClose = true;
        dialogConfig.width = '650px';
        dialogConfig.height = '460px';
        dialogConfig.data = this.form;


        const dialogRef = this.dialog.open(NameFormFieldsDialog, dialogConfig);
        dialogRef.afterClosed().subscribe(result => {
            if (result != null) {
                this.save();
            }
        });
    }

    // Helper methods.
    private performFieldDrop(dropCell: LayoutCell, definition: FieldDefinition, existingField: FormField = null): FormField {
        if (dropCell.formFieldId > 0 || dropCell.name != '') {
            this.form.formFields = this.form.formFields.filter(x => x.name != dropCell.name);
        }

        let field: FormField = existingField;
        if (field == null)
            field = this.addField(dropCell, definition);
        else
            this.addExistingField(dropCell, field);

        dropCell.formFieldId = field.id;
        dropCell.name = field.name;

        return field;
    }

    private performBeginAndEndElementDrop(row: LayoutRow, dropCell: LayoutCell, definition: FieldDefinition): void {
        // Is the current row blank?  If so, we will create one additional
        // blank row.  If not, we will create two new blank rows.
        let dropRowIsBlank: boolean = !row.hasContent;
        let numBlankRowsToCreate = (dropRowIsBlank ? 2 : 3);

        // Create the correct number of blank rows.
        let beginEndRows: LayoutRow[] = (dropRowIsBlank ? [row] : []);
        let addRowRelativeToRow: LayoutRow = row;
        for (let index: number = 0; index < numBlankRowsToCreate; index++) {
            let newRow: LayoutRow = this.form.formLayout.addRowBelow(addRowRelativeToRow);
            addRowRelativeToRow = newRow;
            beginEndRows.push(newRow);
        }

        // Also merge all cells in the new row(s).
        this.mergeRowCellsClick(beginEndRows[0], false);
        this.mergeRowCellsClick(beginEndRows[2], false);

        // Create begin and end field definitions.
        // Place the begin and end elements in the blank rows.
        let beginField: FormField = this.createFieldFromDefinition(definition);
        beginField.isConditionalBeginElement = true;
        beginField.isConditionalEndElement = false;

        this.performFieldDrop(beginEndRows[0].cells[0], definition, beginField);

        let endField: FormField = this.createFieldFromDefinition(definition);
        endField.isConditionalBeginElement = false;
        endField.isConditionalEndElement = true;

        endField.matchingBeginOrEndFieldName = beginField.name;
        endField.transientMatchingBeginOrEndField = beginField;
        beginField.matchingBeginOrEndFieldName = endField.name;
        beginField.transientMatchingBeginOrEndField = endField;

        this.performFieldDrop(beginEndRows[2].cells[0], definition, endField);
    }
    private performSpecialFormAction(row: LayoutRow, dropCell: LayoutCell, definition: FieldDefinition, objectDroppedFlags: FormObjectDroppedFlags): void {
        // Note:  plan to move the following logic into the field definition classes themselves.
        if (definition.specialFormAction == FormDefinitionSpecialAction.AddRowAbove) {
            this.form.formLayout.addRowAbove(row);
        } else if (definition.specialFormAction == FormDefinitionSpecialAction.AddRowBelow) {
            this.form.formLayout.addRowBelow(row);
        }

        objectDroppedFlags.skipAddLastRow = true;
    }

    private addField(cell: LayoutCell, definition: FieldDefinition): FormField {
        let formField: FormField = this.createFieldFromDefinition(definition);

        return this.addExistingField(cell, formField);
    }
    private addExistingField(cell: LayoutCell, formField: FormField): FormField {
        this.form.formFields.push(formField);
        this.flagDirty();

        cell.formFieldId = formField.id;
        cell.name = formField.name;

        return formField;
    }
    private createFieldFromDefinition(definition: FieldDefinition): FormField {
        let formField: FormField = new FormField();

        formField.fieldDefinitionClassName = definition.className;
        formField.name = this.getUniqueName(definition);
        formField.clientId = this.getNextUniqueFormFieldClientId(); // 09-18-2020 note:  added this line.
        formField.iconName = definition.iconName;
        formField.iconType = definition.iconType;
        formField.cannotBeSplitOnForm = definition.cannotBeSplitOnForm;

        return formField;
    }

    private undeleteField(dropCell: LayoutCell, field: FormField): FormField {

        let formField: FormField = new FormField();
        formField.name = field.toolTip;
        formField.displayName = field.displayName;
        formField.clientId = this.getNextUniqueFormFieldClientId();
        formField.id = field.id;
        formField.fieldDefinitionClassName = field.fieldDefinitionClassName;

        this.form.formFields.push(formField);
        this.flagDirty();

        dropCell.formFieldId = formField.id;
        dropCell.name = formField.name;

        return formField;
    }

    private getUniqueName(definition: FieldDefinition): string {
        let count = this.form.formFields.filter(x => x.fieldDefinitionClassName == definition.className).length;

        let uniqueName = definition.displayName.replace(' ', '') + count.toString();

        while (this.form.formFields.filter(x => x.name == uniqueName).length > 0) {
            count = count + 1;

            //VNEXT-529: KLW - This variable was being declared instead of set, so uniqueName was never being properly updated
            uniqueName = definition.displayName.replace(' ', '') + count.toString();

            //VNEXT-529: KLW - Even though the infinite loop issue was fixed, put this as an absolute fail safe to prevent any possiblity in the future
            if (count > 10000)
                break;
        }

        return uniqueName;
    }

    private selectCell(cell: LayoutCell): void {
        this.form.formLayout.clearSelection();
        cell.isSelected = true;
    }
    //@Scriptable()
    public selectCellCoordinate(rowNum: number, cellNum: number): boolean { // Note:  uses zero-based indices.
        let selectionPerformed: boolean = false;

        if ((rowNum >= 0) && (cellNum >= 0)) {
            let row: LayoutRow = this.form.formLayout.rows[rowNum];

            if ((row.cells != null) && (cellNum < row.cells.length)) {
                let cell: LayoutCell = row.cells[cellNum];
                cell.isSelected = true;
                selectionPerformed = true;
            }
        }

        return selectionPerformed;
    }
    @Scriptable('Toggle Always Show Cell Selection')
    public toggleAlwaysShowCellSelection(): void {
        this.form.alwaysShowCellSelection = !this.form.alwaysShowCellSelection;
    }
    @Scriptable('Select First Cell')
    public selectFirstCell(): boolean {
        return this.form.formLayout.selectFirstCell();
    }
    @Scriptable('Select Next Cell')
    public selectNextCell(): boolean {
        return this.form.formLayout.selectNextCell();
    }
    @Scriptable('Select Last Cell')
    public selectLastCell(): boolean {
        return this.form.formLayout.selectLastCell();
    }
    @Scriptable('Create Field', ['Field Type', 'Field Display Name'], ['FormFieldTypeEnum', null])
    public createFieldInSelectedCell(formFieldType: FormFieldTypeEnum, fieldDisplayName: string): boolean {
        let fieldCreated: boolean = false;

        let fieldDefinition: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(formFieldType);
        let selectedCellInfo: LayoutCellInfo = this.form.formLayout.selectedCellInfo;
        if (selectedCellInfo != null) {
            let field: FormField = this.performFieldDrop(selectedCellInfo.cell, fieldDefinition);
            field.displayName = fieldDisplayName;
            fieldCreated = true;

            let row: LayoutRow = this.form.formLayout.rows[selectedCellInfo.rowIndex];
            if (row.isLastRow) {
                row.isLastRow = false;
                this.form.formLayout.addBlankRow();
            }
        }

        return fieldCreated;
    }
    @Scriptable('Edit Cell Properties')
    public editSelectedCellProperties(): boolean {
        let propertiesOpened: boolean = false;

        let selectedCellOffset: number = this.form.formLayout.getCellOffset();

        if ((selectedCellOffset >= 0) && (this.formFieldWrappers != null)) {
            let formFieldWrappers: FormFieldWrapperComponent[] = this.formFieldWrappers.toArray();

            if (selectedCellOffset < formFieldWrappers.length) {
                let selectedFormFieldWrapper: FormFieldWrapperComponent = formFieldWrappers[selectedCellOffset];
                selectedFormFieldWrapper.handleEditPropertiesClickEvent();
                propertiesOpened = true;
            }
        }

        return propertiesOpened;
    }

    // Helper methods.
    private deleteCellContent(cell: LayoutCell): void {
        //debugger;
        // Note:  reuse the code from the 'forEach' in the prior method.
        let fieldDefinitionClassName: string = null;
        let ff: FormField = (cell.formFieldId != null) && (cell.formFieldId != 0) ? this.getFormField(cell.formFieldId, cell.name) : null;
        if (ff) {
            fieldDefinitionClassName = ff.fieldDefinitionClassName;

            ff.isDeleted = true;

            let delField = new FormField;
            delField.id = ff.id;
            delField.iconName = ff.iconName;
            delField.displayName = ff.displayName;
            if (!delField.displayName) delField.displayName = ff.name;
            delField.iconType = ff.iconType;
            delField.toolTip = ff.name;
            delField.modifiedDate = new Date();
            delField.name = ff.name;
            delField.fieldDefinitionClassName = ff.fieldDefinitionClassName;

            this.deletedFields.push(delField);

            // Delete any conditional logic rules that
            // are triggered by or affect this field.
            this.deleteAnyConditionalRulesForField(delField);
        }
        // Remove the cell from the layout row.
        cell.formFieldId = 0;
        cell.name = null;
        this.flagDirty();

        // Notify the form field's field definition object
        // that a field of its type was deleted.  This allows
        // the field definition object to perform any related
        // logic, e.g. in the case of "Show To" and "Hide From"
        // fields that always appear on a form definition in pairs.
        if (fieldDefinitionClassName != null) {
            let fieldDefinition: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldClientLogicHandler(fieldDefinitionClassName);
            fieldDefinition.fieldDraggedToTrash(ff, this.form);
        }

        return;
    }

    private deleteAnyConditionalRulesForField(delField: FormField): void {
        // Delete any conditional logic rules that
        // are triggered by or affect this field.
        if (this.form.conditionalLogicRules != null) {
            this.form.conditionalLogicRules = this.form.conditionalLogicRules.filter(r => r.sourceFieldName != delField.name);

            let replacementRules: FormFieldExpression[] = [];

            for (let index: number = 0; index < this.form.conditionalLogicRules.length; index++) {
                let removeRule: boolean = false;
                let rule: FormFieldExpression = this.form.conditionalLogicRules[index];
                if (rule.actions != null) {
                    for (let iAction: number = 0; iAction < rule.actions.length; iAction++) {
                        let action: FormFieldAction = rule.actions[iAction];
                        if (action.targetFieldName == delField.name) {
                            removeRule = true;
                            break;
                        }
                    }
                }

                if (!removeRule)
                    replacementRules.push(rule);
            }

            this.form.conditionalLogicRules = replacementRules;
        }
    }

    // Define implement private helper methods.
    private tellFormFieldsToSave(reasonForSave: FormFieldConfigurationSaveReason): void {
        let childFormFieldWrappers: FormFieldWrapperComponent[] = this.childFormFieldWrapperComponents;

        for (let iComponent: number = 0; iComponent < childFormFieldWrappers.length; iComponent++) {
            let component: FormFieldBaseComponent = childFormFieldWrappers[iComponent];

            component.saveConfiguration(this.form, reasonForSave);
        }

        return;
    }
    private tellFormFieldsSaveCompleted(): void {
        let formFieldWrappers: FormFieldWrapperComponent[] = this.childFormFieldWrapperComponents;
        for (let index: number = 0; index < formFieldWrappers.length; index++) {
            let formFieldWrapper: FormFieldWrapperComponent = formFieldWrappers[index];
            formFieldWrapper.saveConfigurationCompleted(this.form);
        }
    }

    public notifyFormFieldOfPropertyUpdate(formField: FormField, propertyName: string): void {
        let childFormFieldWrappers: FormFieldWrapperComponent[] = this.childFormFieldWrapperComponents;

        for (let iComponent: number = 0; iComponent < this.childFormFieldWrapperComponents.length; iComponent++) {
            let component: FormFieldBaseComponent = childFormFieldWrappers[iComponent];

            // Need to check to see if the component is the one for
            // which the property panel / drawer is being displayed.
            if (component === this.formFieldProperties.selectedComponent) {
                component.propertyUpdated(formField, propertyName);
            } // if
        } // for

        return;
    }

    private resetCellsWithNoFormField(): void {
        //debugger;
        for (let iRow: number = 0; iRow < this.form.formLayout.rows.length; iRow++) {
            let row: LayoutRow = this.form.formLayout.rows[iRow];

            for (let iCell: number = 0; iCell < row.cells.length; iCell++) {
                let cell: LayoutCell = row.cells[iCell];

                if ((cell.formFieldId > 0) || ((cell.name != null) && (cell.name.trim() != ''))) {
                    let formField: FormField = this.getFormField(cell.formFieldId, cell.name);

                    if (formField == null) {
                        let errorMessage: string = `FormBuilder.validateLayoutCells():  cannot find a form field for cell '${cell.name}' ` +
                            `with Id value ${cell.formFieldId} in row ${iRow}, cell ${iCell}.`;
                        console.log(errorMessage);

                        cell.formFieldId = 0;
                        cell.name = null;
                    }
                }
            }
        }
    }

    private removeFieldsNotInLayout(): void {
        let startingFieldCount: number = (this.form.formFields ? this.form.formFields.length : 0);
        let endingFieldCount: number = 0; // Calculated below.

        if (this.form.formFields) {

            let fieldsNotInLayout: FormField[] = [];

            // Let's get rid of any field not referenced in the form's layout.
            for (let iFormField: number = 0; iFormField < this.form.formFields.length; iFormField++) {
                let formField: FormField = this.form.formFields[iFormField];

                // Lookup for this field in the form's layout.
                let bFieldFound: boolean = false;
                for (let iRow: number = 0; iRow < this.form.formLayout.rows.length; iRow++) {
                    let row: LayoutRow = this.form.formLayout.rows[iRow];

                    for (let iCell: number = 0; iCell < row.cells.length; iCell++) {
                        let cell: LayoutCell = row.cells[iCell];

                        if ((cell.formFieldId == formField.id) || (cell.name = formField.name)) {
                            bFieldFound = true;

                            if ((cell.name == 'c1') || (cell.name == 'c2') || (cell.name == 'c3') || (cell.name == 'c4') || (cell.name == 'c5') || (cell.name == 'c6')) {
                                console.log(`Found field '${cell.name}' in the form layout.`);
                            }

                            break;
                        } // if
                    } // for
                } // for

                if (!bFieldFound) {
                    fieldsNotInLayout.push(formField);
                }
            } // for

            // Remove any unused fields.
            for (let iFormField: number = 0; iFormField < fieldsNotInLayout.length; iFormField++) {
                let formField: FormField = fieldsNotInLayout[iFormField];

                if (formField.id > 0) {
                    this.form.formFields = this.form.formFields.filter(ff => ff.id != formField.id);
                } else {
                    this.form.formFields = this.form.formFields.filter(ff => ff.name != formField.name);
                }
            }

            endingFieldCount = this.form.formFields.length;
        } // if

        return;
    }

    private assignFormFieldClientIds(): void {
        let iNextClientId: number = 1;

        for (let iField: number = 0; iField < this.form.formFields.length; iField++) {
            let formField: FormField = this.form.formFields[iField];

            formField.clientId = iNextClientId;

            iNextClientId++;
        }

        return;
    }

    private getNextUniqueFormFieldClientId(): number {
        let iUniqueId: number = 0;

        if ((!this.form.formFields) || (this.form.formFields.length == 0)) {
            iUniqueId = 1;
        } else {
            for (let iField: number = 0; iField < this.form.formFields.length; iField++) {
                let formField: FormField = this.form.formFields[iField];

                if (formField.clientId >= iUniqueId) {
                    iUniqueId = formField.clientId + 1;
                }
            }
        }

        return (iUniqueId);
    }

    // Note:  is the following method even necessary anymore?
    //
    //        With a bit of review, we should be able to delete the following method.
    private assignConditionalLogicRuleClientIds(): void {
        // For any rule/expression, get the
        // corresponding form field's client Id.
        if ((this.form.conditionalLogicRules) && (this.form.conditionalLogicRules.length > 0)) {
            // Index the form field's by client Id.
            let hshFormFieldsById: IFormFieldIdToFormField = this.getFormFieldsByIdHash();

            // Assign rule and rule action form field client Id values.
            for (let iRule: number = 0; iRule < this.form.conditionalLogicRules.length; iRule++) {
                let rule: FormFieldExpression = this.form.conditionalLogicRules[iRule];

                let sourceFormField: FormField = hshFormFieldsById[rule.sourceFieldId];
                if (sourceFormField == null) {
                    let errorMsg: string = `FormBuilderComponent.assignConditionalLogicRuleClientIds():  could not find source ` + `form field '${rule.sourceFieldName}' by its is value of ${rule.sourceFieldId}.`;
                    throw (errorMsg);
                }

                if ((rule.actions) && (rule.actions.length > 0)) {
                    for (let iAction: number = 0; iAction < rule.actions.length; iAction++) {
                        let action: FormFieldAction = rule.actions[iAction];

                        let targetFormField: FormField = hshFormFieldsById[action.targetFieldId];
                        if (targetFormField == null) {
                            let errorMsg: string = `FormBuilderComponent.assignConditionalLogicRuleClientIds():  could not find target form field '${action.targetFieldName}' by its is value of ${action.targetFieldId}.`;
                            throw (errorMsg);
                        }

                        //action.targetFieldClientId = targetFormField.clientId;
                    } // for
                } // if
            } // for
        } // if

        return;
    }

    private loadDeletedFormFields() {
        this.deletedFields = [];
        if (this.form.formFieldsDeleted) {
            this.form.formFieldsDeleted.forEach(df => {
                let delField = new FormField;
                delField.id = df.id;
                delField.iconName = df.iconName;
                delField.displayName = df.displayName;
                if (!delField.displayName) delField.displayName = df.name;
                delField.name = df.name;
                delField.iconType = df.iconType;
                delField.toolTip = df.name;
                delField.modifiedDate = df.modifiedDate;
                delField.fieldDefinitionClassName = df.fieldDefinitionClassName;
                this.deletedFields.push(delField);
            });
        }
    }

    private getFormFieldsByIdHash(): IFormFieldIdToFormField {
        let hshFormFieldsByClientId: IFormFieldIdToFormField = this.form.getFormFieldsByIdHash();

        return (hshFormFieldsByClientId);
    }

    private get childFormFieldWrapperComponents(): FormFieldWrapperComponent[] {
        let formFieldWrappers: FormFieldWrapperComponent[] = [];

        if (this.formFieldWrappers != null)
            formFieldWrappers = this.formFieldWrappers.toArray();

        return formFieldWrappers;
    }

    private customGetTextForMatListItem(driver: IBrowserDriver, element: object, existingTextValue: string, additionalElementInfo: IAdditionalElementInfo): string {
        let text: string = existingTextValue;

        if ((element != null) && ($(element).length > 0) && (element['firstChild'] != null) && (element['firstChild']['lastChild'] != null))
            text = element['firstChild']['lastChild'].innerHTML;

        return text;
    }
    private setupSearchForFieldGroupMatListItems(): void {
        if (this.fieldGroups != null) {
            for (let index: number = 0; index < this.fieldGroups.length; index++) {
                let fieldGroup: string = this.sanitizeFieldGroupName(this.fieldGroups[index]);
                this.element(`#${fieldGroup}`).hasMatListItems(new AdditionalElementInfo({ customGetTextForFunction: this.customGetTextForMatListItem, useDoubleClick: true, cssFilter: 'visibility: visible' })); //.withCssFilter('visibility: visible');
            }
        }
    }

    public editTemplateNameClick(): void {
        this.isEditingFormName = true;
        this.originalName = this.form.name;

        //**Set original form name for isDirty check**/
        if (this.SavedFormCopy == null)
            this.SavedFormCopy = <Form>this.form.clone();

        setTimeout(() => {
            this.formNameInput.nativeElement.focus();
            this.formNameInput.nativeElement.select();
        }, 0);
    }

    public applyFormTemplateName(): void {
        this.isEditingFormName = false;
        this.form.name = this.form.name.trim();

        if (this.SavedFormCopy.name != this.form.name)
            this.flagDirty();

        // 07-03-2024 note:  need to reset this.editFormData.savedFormCopy to null because there is code in method drawerClosing(),
        //                   the "else if" branch, that will overwrite the form with a previously saved version of the form,
        //                   in this case the previous verion created in the prior method.
        this.editFormData.savedFormCopy = null; // Fix for VNEXT-1344
    }

    public cancelRename() {
        this.isEditingFormName = false;
        this.form.name = this.originalName;
    }
}
